import {
  PAYROLL_ROSTER_FIELD_TYPE,
  ROSTER_FILTER_KIND,
  type PayrollRosterFieldFilters,
  type PayrollRosterDraftMember,
  type PayrollRosterField,
  type PayrollRosterRowFocus,
  type PayrollRosterValue,
  type RosterFieldFilter,
} from "@/payroll/roster/types";
import { isBlankRosterValue } from "@/payroll/roster/utils/members";
import { isRosterFilterActive, parseRosterRangeBound } from "./filter-state";

export const rosterValueKey = (
  value: PayrollRosterValue | undefined,
  field: PayrollRosterField,
): string => {
  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return String(numeric);
  }
  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.boolean) {
    return value === true || value === "true" ? "true" : "false";
  }
  return String(value);
};

const matchesRosterFilter = (
  value: PayrollRosterValue | undefined,
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): boolean => {
  if (isBlankRosterValue(value)) return filter.includeBlank;

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.options:
      return filter.values.includes(rosterValueKey(value, field));
    case ROSTER_FILTER_KIND.boolean:
      return filter.values.includes(value === true || value === "true");
    case ROSTER_FILTER_KIND.range: {
      const min = parseRosterRangeBound(filter.min);
      const max = parseRosterRangeBound(filter.max);
      if (min === null && max === null) return false;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return false;
      return (
        (min === null || numeric >= min) && (max === null || numeric <= max)
      );
    }
    case ROSTER_FILTER_KIND.text: {
      const query = filter.query.trim().toLocaleLowerCase();
      if (!query) return false;
      return String(value).toLocaleLowerCase().includes(query);
    }
  }
};

export const filterPayrollRosterDrafts = <
  T extends Pick<PayrollRosterDraftMember, "member">,
>(
  drafts: T[],
  fields: PayrollRosterField[],
  search: string,
  filters: PayrollRosterFieldFilters,
): T[] => {
  const query = search.trim().toLocaleLowerCase();
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  const active = Object.entries(filters).flatMap(([fieldKey, filter]) => {
    const field = fieldsByKey.get(fieldKey);
    return field && isRosterFilterActive(filter)
      ? [[field, filter] as const]
      : [];
  });

  if (!query && active.length === 0) return drafts;

  return drafts.filter(({ member }) => {
    if (
      query &&
      !`${member.display_name} ${member.service_titan_employee_id}`
        .toLocaleLowerCase()
        .includes(query)
    ) {
      return false;
    }

    return active.every(([field, filter]) =>
      matchesRosterFilter(member.field_values[field.key], field, filter),
    );
  });
};

export const filterPayrollRosterDraftsByFocus = (
  drafts: PayrollRosterDraftMember[],
  focus: PayrollRosterRowFocus,
  departedIds: Set<string>,
): PayrollRosterDraftMember[] => {
  if (focus === "missing") {
    return drafts.filter((draft) => draft.suggestion);
  }
  if (focus === "departed") {
    return drafts.filter(
      (draft) =>
        !draft.suggestion &&
        departedIds.has(draft.member.service_titan_employee_id),
    );
  }
  return drafts;
};
